import React, { useState, useEffect } from 'react'
import { 
  RefreshCw, 
  Server, 
  Database, 
  Cloud, 
  Link2, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle 
} from 'lucide-react'
import AdminLayout from '../../components/Admin/AdminLayout'
import { Button } from '../../components/ui/button'

// Types for health check
type ServiceStatus = 'healthy' | 'warning' | 'critical'

interface ServiceHealthCheck {
  name: string
  status: ServiceStatus
  lastChecked: string
  responseTime: number
  details?: string
}

const SystemHealthPage: React.FC = () => {
  const [services, setServices] = useState<ServiceHealthCheck[]>([
    { 
      name: 'Database', 
      status: 'healthy', 
      lastChecked: new Date().toLocaleString(), 
      responseTime: 120 
    },
    { 
      name: 'API Server', 
      status: 'healthy', 
      lastChecked: new Date().toLocaleString(), 
      responseTime: 80 
    },
    { 
      name: 'External Services', 
      status: 'warning', 
      lastChecked: new Date().toLocaleString(), 
      responseTime: 450,
      details: 'Spoonacular API experiencing slowness' 
    },
    { 
      name: 'Authentication', 
      status: 'healthy', 
      lastChecked: new Date().toLocaleString(), 
      responseTime: 100 
    }
  ])

  const [isRefreshing, setIsRefreshing] = useState(false)

  const getStatusColor = (status: ServiceStatus) => {
    switch (status) {
      case 'healthy': return 'bg-green-500'
      case 'warning': return 'bg-yellow-500'
      case 'critical': return 'bg-red-500'
    }
  }

  const getStatusIcon = (status: ServiceStatus) => {
    switch (status) {
      case 'healthy': return <CheckCircle2 className="text-green-500" />
      case 'warning': return <AlertTriangle className="text-yellow-500" />
      case 'critical': return <XCircle className="text-red-500" />
    }
  }

  const performHealthCheck = async () => {
    setIsRefreshing(true)
    try {
      // Simulate health check - in real implementation, call actual health check endpoints
      const updatedServices = services.map(service => ({
        ...service,
        lastChecked: new Date().toLocaleString(),
        responseTime: Math.floor(Math.random() * 500) + 50
      }))
      setServices(updatedServices)
    } catch (error) {
      console.error('Health check failed', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    const interval = setInterval(performHealthCheck, 30000) // Auto-refresh every 30 seconds
    return () => clearInterval(interval)
  }, [])

  return (
    <AdminLayout title="System Health">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">System Health Dashboard</h2>
          <Button 
            onClick={performHealthCheck} 
            disabled={isRefreshing}
            variant="outline"
          >
            <RefreshCw className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Checking...' : 'Refresh Health Status'}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="bg-white border border-stone/20 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-3">
                  {service.name === 'Database' && <Database className="w-5 h-5 text-slate-600" />}
                  {service.name === 'API Server' && <Server className="w-5 h-5 text-slate-600" />}
                  {service.name === 'External Services' && <Cloud className="w-5 h-5 text-slate-600" />}
                  {service.name === 'Authentication' && <Link2 className="w-5 h-5 text-slate-600" />}
                  <h3 className="font-medium">{service.name}</h3>
                </div>
                {getStatusIcon(service.status)}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Status</span>
                  <span className={`px-2 py-0.5 rounded ${getStatusColor(service.status)} text-white`}>
                    {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Last Checked</span>
                  <span>{service.lastChecked}</span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Response Time</span>
                  <span>{service.responseTime} ms</span>
                </div>
                {service.details && (
                  <div className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded mt-2">
                    {service.details}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-stone/20 rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Health Check History</h3>
          {/* Placeholder for a more comprehensive health check history table */}
          <div className="text-sm text-slate-500">
            Detailed health check logs will be implemented in a future update.
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

export default SystemHealthPage